import { Box, createStyles, Group, Image, Stack, Text } from '@mantine/core';
import { isBoolean } from 'lodash/fp';
import React, { useMemo } from 'react';

import { useConfig } from '@portals/api/partners';
import { TenantType } from '@portals/types';

import { StoreSkeleton } from './PortalCapabilitiesStep/PortalSkeleton';
import { SidebarMock } from './SidebarMock';
import { useDesignField } from '../../pages/branding/branding-forms/customer-portal/tool-panel/tabs/design/fields/hooks';
import { useBrandingContext } from '../../pages/branding/branding.context';

export function StoreBrowserMock() {
  const { classes, cx } = useStyles();
  const { portalConfig, defaultValues } = useBrandingContext();
  const config = useConfig();

  const isWithBanner = useMemo(() => {
    if (!isBoolean(portalConfig.design?.store?.with_banner)) {
      return defaultValues.design.store.with_banner;
    }

    return portalConfig.design?.store?.with_banner;
  }, [
    defaultValues.design.store.with_banner,
    portalConfig.design?.store?.with_banner,
  ]);

  const logoSrc = useDesignField(['design', 'store', 'logo']);
  const bannerSrc = useDesignField(['design', 'store', 'banner']);

  return (
    <Box className={classes.container}>
      <SidebarMock />

      <Box
        className={cx(classes.content, { 'with-banner': isWithBanner })}
        bg="gray.0"
      >
        {/* Navbar */}
        <Group
          h={40}
          bg="white"
          sx={(theme) => ({
            borderBottom: `2px solid ${theme.colors.blue_gray[0]}`,
          })}
        />

        {/* Banner */}
        {isWithBanner ? (
          <Image
            h={100}
            w="100%"
            src={bannerSrc.value || bannerSrc.defaultValue}
            fit="cover"
            imageProps={{
              style: {
                height: 100,
                aspectRatio: '8',
              },
            }}
          />
        ) : null}

        {/* Header */}
        <Group
          h={90}
          bg="white"
          p="md"
          align="center"
          spacing="md"
          noWrap
          sx={(theme) => ({
            overflow: 'hidden',
            borderBottom: `1px solid ${theme.colors.gray[2]}`,
          })}
        >
          {/* Header - Partner Logo */}
          <Image
            radius="md"
            w="60px !important"
            height={60}
            width={60}
            src={logoSrc.value || logoSrc.defaultValue}
            fit="contain"
            imageProps={{
              style: {
                width: 60,
                height: 60,
              },
            }}
          />

          {/* Header - Partner name & store tagline */}
          <Stack
            justify="center"
            spacing="xs"
            mah={60}
            sx={{ overflow: 'hidden' }}
          >
            <Text size="lg" color="blue_gray.8" weight={500} truncate>
              {config?.data?.[TenantType.Partner]?.display_name}
            </Text>

            {portalConfig?.design?.store?.tagline ? (
              <Text size="sm" color="blue_gray.4" truncate>
                {portalConfig?.design?.store?.tagline}
              </Text>
            ) : null}
          </Stack>
        </Group>

        <StoreSkeleton />
      </Box>
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'grid',
    gridTemplateRows: '100%',
    gridTemplateColumns: '195px 1fr',
    background: theme.white,
  },
  content: {
    display: 'grid',
    gridTemplateRows: '40px 90px 1fr',

    '&.with-banner': {
      gridTemplateRows: '40px 100px 90px 1fr',
    },
  },
}));
